<template>
	<section>
		<h2 class="font-display text-6 my-4">Usage Wallet</h2>
		<p class="text-3 my-4">Estimate your monthly usage and add credit to your Usage Wallet to avoid monthly invoices. Funds will only be deducted from your usage wallet based on actual monthly usage.</p>
		<CCustomizePlanAddon
			v-for="card in walletAddOns"
			:key="card.key"
			:value="quantities[card.model]"
			v-bind="card.props"
			class="mb-4"
			@input="$emit('update-quantity', { model: card.model, value: $event })"
			@info-click="onInfoClick(card)"
		/>
		<CCustomizePlanAddon
			title="Additional Funds"
			description="Add an annual fixed amount to your usage wallet."
			label="dollars"
			icon="complex-icons/wallet"
			:min="0"
			:max="100000"
			:slider="false"
			:decimals="2"
			:value="quantities.additionalFunds"
			class="mb-4"
			@input="$emit('update-quantity', { model: 'additionalFunds', value: $event })"
		/>
		<p v-if="showSuggestedAmounts">
			<span class="text-icon-subtle">▼</span> Suggested amounts based on
			<ElPopover
				placement="top"
				trigger="click"
				popper-class="border border-primary bg-primary p-3 shadow"
			>
				<template #reference>
					<button class="button button--no-style text-link underline">your organization's historical usage</button>
				</template>
				<template #default>
					<h2 class="text-2 mb-3">Monthly Average <small v-if="usageWindowLabel" class="font-normal">({{ usageWindowLabel }})</small></h2>
					<table class="table-style w-full">
						<tr>
							<th>Feature</th>
							<th>Avg. Monthly Usage</th>
						</tr>
						<tr v-for="row in averageRows" :key="row.label">
							<td>
								<div class="flex items-baseline gap-3">
									<Inline :svg="row.icon" class="text-icon-subtle self-center" />
									{{ row.label }}
								</div>
							</td>
							<td>{{ formatAverage(row.value, row.decimals) }}{{ row.suffix }}</td>
						</tr>
					</table>
				</template>
			</ElPopover>.
		</p>
		<CDialog
			v-if="plan"
			:visible="guestDialogVisible"
			max-width="1000px"
			@closed="guestDialogVisible = false"
		>
			<CGuestPricing
				:plan="plan"
				:current-plan-name="currentPlanName"
				@close="guestDialogVisible = false"
			/>
		</CDialog>
	</section>
</template>

<script lang="ts">
	import { defineComponent, ref, type PropType } from 'vue';
	import CCustomizePlanAddon from './CustomizePlanAddon.vue';
	import type { WalletQuantities } from './use-wallet-addons';
	import { Popover } from '@cognitoforms/element-ui';
	import CDialog from 'src/components/dialogs/Dialog.vue';
	import CGuestPricing from 'src/views/pricing/GuestPricing.vue';
	import type { Plan } from '@cognitoforms/types/server-types/plans/model/plan';

	interface AverageRow {
		icon: string;
		label: string;
		value: number;
		decimals: number;
		suffix: string;
	}

	interface WalletAddOn {
		key: string;
		model: keyof WalletQuantities;
		props: Record<string, unknown>;
	}

	export default defineComponent({
		name: 'CWalletCustomizeSection',
		components: { CCustomizePlanAddon, ElPopover: Popover, CDialog, CGuestPricing },
		props: {
			walletAddOns: { type: Array as PropType<WalletAddOn[]>, required: true },
			quantities: { type: Object as PropType<WalletQuantities>, required: true },
			averageRows: { type: Array as PropType<AverageRow[]>, required: true },
			usageWindowLabel: { type: String, default: '' },
			plan: { type: Object as PropType<Plan | null>, default: null },
			currentPlanName: { type: String, default: null },
			showSuggestedAmounts: { type: Boolean, default: true }
		},
		emits: ['update-quantity'],
		setup() {
			const formatAverage = (value: number, decimals = 0) => value.toLocaleString('en-us', { maximumFractionDigits: decimals });

			const guestDialogVisible = ref(false);
			const onInfoClick = (card: WalletAddOn) => {
				if (card.model === 'guests')
					guestDialogVisible.value = true;
			};

			return { formatAverage, guestDialogVisible, onInfoClick };
		}
	});
</script>
